/**
 * @package @api-buddy/supabase
 * Supabase client and utilities for API Buddy
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
export interface SupabaseConfig {
  url: string;
  anonKey: string;
  options?: {
    auth?: {
      autoRefreshToken?: boolean;
      persistSession?: boolean;
      detectSessionInUrl?: boolean;
    };
  };
}

/**
 * Extended Supabase client with API Buddy specific functionality
 */
export class SupabaseService {
  private client: SupabaseClient;
  private logger: {
    info: (message: string) => void;
    error: (message: string, error?: any) => void;
  };

  constructor(config: SupabaseConfig, logger?: any) {
    this.client = createClient(config.url, config.anonKey, config.options);
    this.logger = logger || {
      info: (message: string) => console.log(`[Supabase] ${message}`),
      error: (message: string, error?: any) => {
        const errorMessage = error ? `${message}: ${error.message || error}` : message;
        console.error(`[Supabase] ${errorMessage}`);
        if (error?.stack) {
          console.error(error.stack);
        }
      }
    };
  }

  /**
   * Get the underlying Supabase client
   */
  getClient(): SupabaseClient {
    return this.client;
  }

  /**
   * Execute a query with error handling
   */
  async query<T = any>(
    table: string,
    query: string,
    params?: Record<string, unknown>
  ): Promise<T | null> {
    try {
      const { data, error } = await this.client
        .from(table)
        .select(query)
        .match(params || {});

      if (error) {
        throw error;
      }

      this.logger.info(`Successfully executed query on table: ${table}`);
      return data as unknown as T;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.logger.error(`Supabase query error: ${errorMessage}`, { table, query, params });
      return null;
    }
  }
}

/**
 * Create a new Supabase service instance
 */
export function createSupabaseService(
  config: SupabaseConfig,
  logger?: any
): SupabaseService {
  return new SupabaseService(config, logger);
}

export * from '@supabase/supabase-js';
